const WORD_BOUNDARY_REGEX = /(^|[\s\-'.’])([\p{L}])/gu;

const looksLikeIdentifier = (value: string): boolean => {
  if (value.includes(" ")) {
    return false;
  }
  if (!/^[A-Z0-9_-]+$/.test(value)) {
    return false;
  }
  return /[_\d-]/.test(value);
};

/**
 * Surname particles that belong to the surname that follows them. Used to
 * avoid amputating a compound surname ("de la Cruz") down to a dangling
 * connector when the trailing maternal surname is dropped.
 */
const SURNAME_PARTICLES = new Set([
  "de",
  "del",
  "de-la",
  "la",
  "las",
  "los",
  "da",
  "das",
  "do",
  "dos",
  "di",
  "van",
  "von",
  "der",
  "den",
  "y",
  "e",
  "san",
  "santa",
  "mac",
  "mc",
  "st",
  "st.",
]);

/**
 * Particles that stay lowercase when they appear anywhere in a name EXCEPT
 * as the first word (EPIC-95 T4, client repro: Spanish-language surnames
 * — "de la Cruz", "del Valle", "da Silva" — hit this constantly, and the
 * roster importer's own backend already keeps these lowercase mid-name via
 * `titlecase_name` (ProMatchAnalytics-Backend/app/services/roster_import/
 * normalize.py) — this list keeps the frontend's own formatter consistent
 * with that, instead of re-capitalizing an imported "Johan van Dijk" into
 * "Johan Van Dijk" on every render.
 *
 * A particle used AS the first word (e.g. a surname cited alone, "Van
 * Eijma") keeps normal title-case — only a particle preceded by another
 * word is a connector, not the start of the name.
 *
 * Deliberately a separate set from `SURNAME_PARTICLES` below: that one
 * decides where `formatDisplayName` truncates a name (a different
 * question, with entries like "mac"/"mc"/"san" that are usually fused
 * into the surname itself, not casing-relevant as a standalone word).
 */
const CASING_PARTICLES = new Set([
  "van",
  "von",
  "der",
  "den",
  "de",
  "del",
  "della",
  "la",
  "las",
  "los",
  "da",
  "das",
  "do",
  "dos",
  "di",
  "du",
  "le",
  "y",
]);

const titleCaseWord = (word: string): string =>
  word
    .toLocaleLowerCase()
    .replace(
      WORD_BOUNDARY_REGEX,
      (_, prefix: string, letter: string) =>
        `${prefix}${letter.toLocaleUpperCase()}`,
    );

export const formatPlayerName = (rawName?: string | null): string => {
  if (!rawName) {
    return "";
  }

  const trimmed = rawName.trim().replace(/\s+/g, " ");
  if (!trimmed) {
    return "";
  }

  if (looksLikeIdentifier(trimmed)) {
    return trimmed;
  }

  return trimmed
    .split(" ")
    .map((word, index) =>
      index > 0 && CASING_PARTICLES.has(word.toLocaleLowerCase())
        ? word.toLocaleLowerCase()
        : titleCaseWord(word),
    )
    .join(" ");
};

/**
 * Display-only name: hides the second (maternal) surname.
 *
 * Client request 2026-08-06 — Costa Rican rosters are registered with both
 * surnames ("Kendall Gomez Morales"), which overflows pitch chips, the public
 * header timeline and stats tables. This HIDES the second surname at render
 * time only: the stored player record, the players CRUD, and every write
 * payload keep the complete name. Never call this on a value that is about to
 * be persisted.
 *
 * Rule: with three or more tokens, drop the trailing one. Any surname
 * particles left dangling at the end are dropped with it ("Jose Perez de la
 * Cruz" → "Jose Perez"). If that would leave fewer than two tokens the name is
 * returned untouched, which keeps single compound surnames intact ("Juan de la
 * Cruz").
 */
export const formatDisplayName = (rawName?: string | null): string => {
  if (!rawName) {
    return "";
  }

  const trimmed = rawName.trim().replace(/\s+/g, " ");
  if (!trimmed || looksLikeIdentifier(trimmed)) {
    return trimmed;
  }

  const tokens = trimmed.split(" ");
  if (tokens.length < 3) {
    return trimmed;
  }

  // A trailing token with no letters, OR with any digit in it, is a
  // number/marker, never a surname ("Home Player 1", "Juan Perez 10",
  // "E2E Player PLAYER-1786058000000-A3F2B1") — dropping it would hide the
  // only part that distinguishes the entry. A real surname never contains a
  // digit, so this is a more correct rule, not just a test-friendly one.
  const last = tokens[tokens.length - 1];
  if (!/\p{L}/u.test(last) || /\d/.test(last)) {
    return trimmed;
  }

  const kept = tokens.slice(0, -1);
  while (
    kept.length > 0 &&
    SURNAME_PARTICLES.has(kept[kept.length - 1].toLocaleLowerCase())
  ) {
    kept.pop();
  }

  return kept.length < 2 ? trimmed : kept.join(" ");
};
